/**
 * Client-side DIFF ASSEMBLY for the runtime git browser.
 *
 * WHY THIS EXISTS: the baked `lx-git` verb allow-lists no `-p` / `-U`,
 * so **there is no unified diff anywhere in the API** — by design, not
 * by omission (see `ai-docs/lexiun-git.md` §6.4-bis). A commit gives us
 * name-status ⋈ numstat (which paths changed, and by how many lines);
 * the actual text must be reconstructed from TWO blob reads (the file at
 * the parent commit, and at this commit) and aligned here.
 *
 * Everything in this module is PURE — no fetches, no React — so the
 * alignment is unit-testable without a runtime. The fetching half lives
 * in the page; `planBlobReads()` tells it which reads are needed.
 */

import type { GitCommitFile } from "./git-browser-api";

/** Alignment beyond this many lines per side falls back to an unaligned
 *  side-by-side view. The LCS table is O(n×m) — 2000² Int32 is ~16 MB,
 *  already generous for a legal-document repo; past it we degrade
 *  VISIBLY (`degraded: true`) rather than freezing the operator's tab. */
export const MAX_ALIGN_LINES = 2000;

export type DiffRowKind = "same" | "add" | "del" | "change";

/** One rendered row of a side-by-side view. A `same` row carries both
 *  sides; `add`/`del` carry one; `change` pairs a removed line with the
 *  line that replaced it. */
export interface DiffRow {
  kind: DiffRowKind;
  /** 1-based line number on the BEFORE side (null when the row is an
   *  addition — nothing existed there). */
  leftNo: number | null;
  leftText: string | null;
  rightNo: number | null;
  rightText: string | null;
}

export interface DiffResult {
  rows: DiffRow[];
  /** True when the files were too large to align — rows are then a
   *  naive index-pairing and the UI MUST say so, because unhighlighted
   *  rows would otherwise read as "nothing changed here". */
  degraded: boolean;
}

/**
 * Split blob text into display lines.
 *
 * Normalizes CRLF (a .docx-adjacent export or a Windows-authored file
 * would otherwise show a stray ␍ on every line and, worse, compare
 * unequal against an identical LF copy — spurious "changed" rows).
 * A single trailing newline is dropped so "a\nb\n" is 2 lines, not 3.
 */
export function splitLines(content: string): string[] {
  if (content === "") return [];
  const normalized = content.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = normalized.split("\n");
  if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
  return lines;
}

/** Which blob reads a file row needs, and at which path. Renames are the
 *  subtle case: the BEFORE side lives at `oldPath`, so reading `path` at
 *  the parent commit would 404 (or, worse, silently read a different
 *  file that happened to occupy that path). */
export interface BlobPlan {
  needParent: boolean;
  needCurrent: boolean;
  /** Path to read at the parent commit (`oldPath` for a rename/copy). */
  parentPath: string | null;
  /** Path to read at this commit. */
  currentPath: string | null;
}

export function planBlobReads(
  file: Pick<GitCommitFile, "status" | "path" | "oldPath" | "binary">,
  parentSha: string | null,
): BlobPlan {
  // A binary blob is never fetched: the API returns {binary:true,
  // content:null} and there is nothing to align.
  if (file.binary) {
    return { needParent: false, needCurrent: false, parentPath: null, currentPath: null };
  }
  const added = file.status === "A";
  const deleted = file.status === "D";
  // A root commit has no parent — everything in it reads as added.
  const hasParent = parentSha !== null && !added;
  return {
    needParent: hasParent && !added,
    needCurrent: !deleted,
    parentPath: hasParent ? (file.oldPath ?? file.path) : null,
    currentPath: deleted ? null : file.path,
  };
}

/**
 * Align two versions of a file into side-by-side rows via a line-level
 * LCS (the same shape `diff` itself uses), then pair adjacent
 * deletions/additions into `change` rows so a modified line renders on
 * one row instead of two.
 */
export function alignDiff(before: string[], after: string[]): DiffResult {
  if (before.length > MAX_ALIGN_LINES || after.length > MAX_ALIGN_LINES) {
    return { rows: naivePairing(before, after), degraded: true };
  }
  const ops = lcsOps(before, after);
  return { rows: pairRuns(ops), degraded: false };
}

// ─── internals ─────────────────────────────────────────────────────────

type Op =
  | { type: "same"; leftNo: number; rightNo: number; text: string }
  | { type: "del"; leftNo: number; text: string }
  | { type: "add"; rightNo: number; text: string };

/** Classic LCS dynamic program + backtrack. Int32Array keeps the table
 *  compact (a plain number[][] of the same size allocates ~8× more and
 *  is what makes naive JS diffs feel slow at a few thousand lines). */
function lcsOps(a: string[], b: string[]): Op[] {
  const n = a.length;
  const m = b.length;
  const width = m + 1;
  const table = new Int32Array((n + 1) * width);
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      table[i * width + j] =
        a[i] === b[j]
          ? table[(i + 1) * width + (j + 1)] + 1
          : Math.max(table[(i + 1) * width + j], table[i * width + (j + 1)]);
    }
  }
  const ops: Op[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ type: "same", leftNo: i + 1, rightNo: j + 1, text: a[i]! });
      i++;
      j++;
    } else if (table[(i + 1) * width + j]! >= table[i * width + (j + 1)]!) {
      ops.push({ type: "del", leftNo: i + 1, text: a[i]! });
      i++;
    } else {
      ops.push({ type: "add", rightNo: j + 1, text: b[j]! });
      j++;
    }
  }
  while (i < n) {
    ops.push({ type: "del", leftNo: i + 1, text: a[i]! });
    i++;
  }
  while (j < m) {
    ops.push({ type: "add", rightNo: j + 1, text: b[j]! });
    j++;
  }
  return ops;
}

/** Zip each run of deletions with the run of additions that follows it,
 *  so an edited line shows as ONE `change` row (before | after) rather
 *  than a deletion row followed by an unrelated-looking addition row. */
function pairRuns(ops: Op[]): DiffRow[] {
  const rows: DiffRow[] = [];
  let k = 0;
  while (k < ops.length) {
    const op = ops[k]!;
    if (op.type === "same") {
      rows.push({
        kind: "same",
        leftNo: op.leftNo,
        leftText: op.text,
        rightNo: op.rightNo,
        rightText: op.text,
      });
      k++;
      continue;
    }
    // Collect the maximal del-run then the add-run that follows it.
    const dels: Extract<Op, { type: "del" }>[] = [];
    while (k < ops.length && ops[k]!.type === "del") {
      dels.push(ops[k] as Extract<Op, { type: "del" }>);
      k++;
    }
    const adds: Extract<Op, { type: "add" }>[] = [];
    while (k < ops.length && ops[k]!.type === "add") {
      adds.push(ops[k] as Extract<Op, { type: "add" }>);
      k++;
    }
    const paired = Math.min(dels.length, adds.length);
    for (let p = 0; p < paired; p++) {
      rows.push({
        kind: "change",
        leftNo: dels[p]!.leftNo,
        leftText: dels[p]!.text,
        rightNo: adds[p]!.rightNo,
        rightText: adds[p]!.text,
      });
    }
    for (let p = paired; p < dels.length; p++) {
      rows.push({
        kind: "del",
        leftNo: dels[p]!.leftNo,
        leftText: dels[p]!.text,
        rightNo: null,
        rightText: null,
      });
    }
    for (let p = paired; p < adds.length; p++) {
      rows.push({
        kind: "add",
        leftNo: null,
        leftText: null,
        rightNo: adds[p]!.rightNo,
        rightText: adds[p]!.text,
      });
    }
  }
  return rows;
}

/** Degraded fallback for very large files: pair by index and mark every
 *  differing pair as a change. No LCS, so a single inserted line shifts
 *  everything below it — hence `degraded: true` and the UI banner. */
function naivePairing(before: string[], after: string[]): DiffRow[] {
  const rows: DiffRow[] = [];
  const max = Math.max(before.length, after.length);
  for (let i = 0; i < max; i++) {
    const l = i < before.length ? before[i]! : null;
    const r = i < after.length ? after[i]! : null;
    rows.push({
      kind: l === null ? "add" : r === null ? "del" : l === r ? "same" : "change",
      leftNo: l === null ? null : i + 1,
      leftText: l,
      rightNo: r === null ? null : i + 1,
      rightText: r,
    });
  }
  return rows;
}
